import { Countries } from '@/composables/useCountries'
import { company as companyFeature } from '@/lib/features'

type CountryRow = (typeof Countries)[number]

/**
 * Countries the platform offers, i.e. the ones the country select can hold.
 * An empty/missing `availableCountryCodes` means "all".
 */
export function availableCountryRows(): CountryRow[] {
  const allowed = companyFeature?.config?.availableCountryCodes
  if (!allowed?.length) return Countries
  return Countries.filter((c) => allowed.includes(String(c.countryCode).toLowerCase()))
}

/**
 * Resolves an ISO-3166 alpha-2 code, an English country name or a localized
 * country label to a country the platform offers. Returns null for anything else
 * — the scraper can report a country outside the platform's reach (a Polish
 * agency, say), and such a value has no entry in the select.
 */
export function resolveAvailableCountry(
  value: unknown,
  translate?: (key: string) => string
): CountryRow | null {
  if (value == null || value === '') return null
  const text = String(value).trim()
  if (!text) return null

  const isCode = /^[a-zA-Z]{2}$/.test(text)
  return (
    availableCountryRows().find((c) =>
      isCode
        ? String(c.countryCode).toLowerCase() === text.toLowerCase()
        : c.value === text || translate?.(c.text) === text
    ) ?? null
  )
}

/** Lowercase ISO-3166 alpha-2 for the API's `CountryCode` enum, or null. */
export function availableCountryCode(
  value: unknown,
  translate?: (key: string) => string
): string | null {
  const row = resolveAvailableCountry(value, translate)
  return row ? String(row.countryCode).toLowerCase() : null
}
